import React, { useState, useEffect } from "react";
import { Card, CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import { useParams, useNavigate } from "react-router-dom";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getAllRaiseInvoices } from "services/financeService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function InvoiceFullHistory() {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);

  // Invoice Table Columns
  const columns = [
    { name: "S.No", align: "center" },
    // { name: "Date", align: "center" },
    { name: "Invoice ID", align: "left" },
    { name: "Type", align: "left" },
    { name: "Total Sessions", align: "center" },
    { name: "Total Amount", align: "right" },
    { name: "Status", align: "center" },
    { name: "Payment Date", align: "center" },
  ];

  useEffect(() => {
    if (parentId) {
      fetchInvoiceHistory();
    }
  }, [parentId]);

  const fetchInvoiceHistory = async () => {
    try {
      setLoading(true);

      // Fetch all invoices without limit
      const response = await getAllRaiseInvoices({
        parentId: parentId,
        limit: 1000, // Get all invoices
      });

      if (response && response.success && response.data) {
        const invoiceData = response.data;

        // Format rows for table
        const formattedRows = invoiceData.map((invoice, index) => ({
          "S.No": (
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {index + 1}
            </ArgonTypography>
          ),
          // Date: (
          //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
          //     {new Date(invoice.createdAt).toLocaleDateString("en-IN")}
          //   </ArgonTypography>
          // ),
          "Invoice ID": (
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {invoice._id || "N/A"}
            </ArgonTypography>
          ),
          Type: (
            <ArgonTypography variant="caption" fontWeight="medium" color="text">
              {invoice.cycleType || "N/A"}
            </ArgonTypography>
          ),
          "Total Sessions": (
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {invoice.totalClasses || "0"}
            </ArgonTypography>
          ),
          "Total Amount": (
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              ₹ {invoice.totalAmount?.toLocaleString("en-IN") || "0"}
            </ArgonTypography>
          ),
          Status: (
            <ArgonBox
              sx={{
                display: "inline-block",
                px: 1.5,
                py: 0.5,
                borderRadius: "6px",
                backgroundColor:
                  invoice.status === "PAID"
                    ? "#d4edda"
                    : invoice.status === "PENDING"
                    ? "#fff3cd"
                    : invoice.status === "FAILED"
                    ? "#f8d7da"
                    : "#e2e3e5",
                color:
                  invoice.status === "PAID"
                    ? "#155724"
                    : invoice.status === "PENDING"
                    ? "#856404"
                    : invoice.status === "FAILED"
                    ? "#721c24"
                    : "#383d41",
              }}
            >
              <ArgonTypography variant="caption" fontWeight="bold" sx={{ color: "inherit" }}>
                {invoice.status || "UNKNOWN"}
              </ArgonTypography>
            </ArgonBox>
          ),
          "Payment Date": (
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString("en-IN") : "Not Paid"}
            </ArgonTypography>
          ),
        }));

        setInvoices(formattedRows);
      }
    } catch (error) {
      console.error("Error fetching invoice history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            {/* Header with Back Button */}
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <ArgonBox display="flex" alignItems="center" gap={2}>
                <ArgonButton
                  onClick={handleBackClick}
                  variant="outlined"
                  color="dark"
                  size="small"
                  sx={{
                    minWidth: "auto",
                    padding: "8px 12px",
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </ArgonButton>
                <ArgonTypography variant="h5" fontWeight="bold">
                  Complete Invoice History
                </ArgonTypography>
              </ArgonBox>
              <ArgonTypography variant="caption" color="text">
                Total Invoices: {invoices.length}
              </ArgonTypography>
            </ArgonBox>

            {/* Table */}
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {invoices.length > 0 ? (
                <Table columns={columns} rows={invoices} />
              ) : (
                <ArgonBox p={5} textAlign="center">
                  <ArgonTypography variant="h6" color="text">
                    No invoice records found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="secondary" mt={1}>
                    There are no invoices available for this parent.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InvoiceFullHistory;
